"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateKickouts = exports.transformToKickouts = exports.transformToKnapsackItem = exports.calculateKnapsackCapacity = exports.filterNonBreachingReceivables = exports.filterBreachingReceivables = exports.calculateDistanceFactor = exports.calculateWeightFactor = void 0;
const lodash_1 = __importDefault(require("lodash"));
const remeda_1 = require("remeda");
const common_1 = require("@finley-technologies/common");
/**
 * The "weight factor" represents the "weight of an item" in the knapsack problem.
 * To translate from WA problem to a the knapsack problem,
 * we multiply the weight (eligibleExposureLessConcentrationKickout, or other property)
 * by its distance factor
 */
const calculateWeightFactor = ({ weight, distanceFactor, }) => {
    return weight * distanceFactor;
};
exports.calculateWeightFactor = calculateWeightFactor;
/**
 * The distance factor represents the numerical distance
 * between the receivable property of interest (value) and the concentration limit
 */
const calculateDistanceFactor = ({ value, concentrationLimit, }) => {
    return Math.abs(value - concentrationLimit);
};
exports.calculateDistanceFactor = calculateDistanceFactor;
const isBreachingFilter = ({ value, concentrationLimit, limitType, }) => {
    switch (limitType) {
        case 'max':
            return value > concentrationLimit;
        case 'min':
            return value < concentrationLimit;
        default:
            return (0, common_1.finleyAssertUnreachable)(limitType);
    }
};
const isNotBreachingFilter = ({ value, concentrationLimit, limitType, }) => !isBreachingFilter({ value, concentrationLimit, limitType });
const getReceivableFilter = (filterType) => {
    switch (filterType) {
        case 'nonBreaching':
            return isNotBreachingFilter;
        case 'breaching':
            return isBreachingFilter;
        default:
            return (0, common_1.finleyAssertUnreachable)(filterType);
    }
};
const filterReceivables = ({ receivables, limitType, concentrationLimit, filterType, }) => {
    const receivableFilter = getReceivableFilter(filterType);
    return (0, remeda_1.filter)(receivables, ({ value }) => {
        return receivableFilter({ value, concentrationLimit, limitType });
    });
};
/**
 * Return list of receivables that are breaching the concentration limit
 */
const filterBreachingReceivables = ({ receivables, limitType, concentrationLimit, }) => {
    return filterReceivables({
        concentrationLimit,
        limitType,
        receivables,
        filterType: 'breaching',
    });
};
exports.filterBreachingReceivables = filterBreachingReceivables;
/**
 * Return list of receivables that are not breaching the concentration limit
 */
const filterNonBreachingReceivables = ({ receivables, limitType, concentrationLimit, }) => {
    return filterReceivables({
        concentrationLimit,
        limitType,
        receivables,
        filterType: 'nonBreaching',
    });
};
exports.filterNonBreachingReceivables = filterNonBreachingReceivables;
const calculateKnapsackCapacity = ({ receivables, concentrationLimit, }) => (0, remeda_1.reduce)(receivables, (totalCapacity, { value, weight }) => {
    const distanceFactor = (0, exports.calculateDistanceFactor)({
        value,
        concentrationLimit,
    });
    const weightFactor = (0, exports.calculateWeightFactor)({ weight, distanceFactor });
    return totalCapacity + weightFactor;
}, 0);
exports.calculateKnapsackCapacity = calculateKnapsackCapacity;
const transformToKnapsackItem = ({ receivable, concentrationLimit, }) => {
    const { weight, value, finleyReceivableId } = receivable;
    const distanceFactor = (0, exports.calculateDistanceFactor)({
        value,
        concentrationLimit,
    });
    const weightFactor = (0, exports.calculateWeightFactor)({ weight, distanceFactor });
    // In process of translating WA receivable to knapsack item, WA receivable "weight" (eligibleExposureLessConcentrationKickout)
    // becomes the knapsack value, which is weighted by it "weight factor"
    return { val: weight, wt: weightFactor, id: finleyReceivableId };
};
exports.transformToKnapsackItem = transformToKnapsackItem;
const transformToKickouts = ({ breachingReceivables, knapsackResult, }) => {
    const omittedReceivableIds = lodash_1.default.difference((0, remeda_1.map)(breachingReceivables, ({ finleyReceivableId }) => finleyReceivableId), (0, remeda_1.map)(knapsackResult.items, ({ id }) => id));
    const receivableMap = (0, remeda_1.indexBy)(breachingReceivables, ({ finleyReceivableId }) => finleyReceivableId);
    return (0, remeda_1.reduce)(omittedReceivableIds, (kickouts, receivableId) => {
        const receivable = receivableMap[receivableId];
        return Object.assign(Object.assign({}, kickouts), { [receivable.finleyReceivableId]: receivable.eligibleExposureLessConcentrationLimitKickouts });
    }, {});
};
exports.transformToKickouts = transformToKickouts;
/**
 * Brute force algo transforms a weighted average concentration limit test (with discrete kickouts)
 * into the Classical 0/1 Knapsack Problem.
 *
 * A transformation to knapsack problem can easily be done recognizing that the "non-breaching" receivables
 * can used to determine the capacity of the knapsack, while the "breaching" receivables represent the items to optimize with
 * given weights and values.
 *
 * A screenshot of the translation process can be seen in "source/__tests__/waToBruteForceKnapsackTranslation.png"
 */
const calculateKickouts = ({ receivables, limitType, concentrationLimit, }) => {
    const breachingReceivables = (0, exports.filterBreachingReceivables)({
        receivables,
        limitType,
        concentrationLimit,
    });
    const nonBreachingReceivables = (0, exports.filterNonBreachingReceivables)({
        receivables,
        limitType,
        concentrationLimit,
    });
    const capacity = (0, exports.calculateKnapsackCapacity)({
        receivables: nonBreachingReceivables,
        concentrationLimit,
    });
    const knapsackItems = (0, remeda_1.map)(breachingReceivables, (receivable) => (0, exports.transformToKnapsackItem)({ receivable, concentrationLimit }));
    const knapsackResult = common_1.utils.solveKnapsackBruteForce({
        capacity,
        knapsackItems,
    });
    return (0, exports.transformToKickouts)({ breachingReceivables, knapsackResult });
};
exports.calculateKickouts = calculateKickouts;
//# sourceMappingURL=bruteForce.js.map